import { Injectable } from '@angular/core';

import { RidangoEpic, RidangoReduxAction } from '@ridango/core';

import momenttz from 'moment-timezone';
import { ActionsObservable } from 'redux-observable';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { BankcardTicketsService } from '../bankcard-tickets.service';
import { BankcardTransaction } from '../model/bankcard-transaction.model';
import { BankcardTicketsActions } from './bankcard-tickets.actions';

@Injectable({
    providedIn: 'root'
})
export class BankcardTicketsEpics implements RidangoEpic {
    constructor(
        private actions: BankcardTicketsActions,
        private service: BankcardTicketsService
    ) {}

    register() {
        return [this.fetchTransaction, this.fetchTickets, this.fetchHslTickets, this.sendFeedback];
    }

    fetchTransaction = (action$: ActionsObservable<any>) =>
        action$.ofType(BankcardTicketsActions.FETCH_TRANSACTION).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.fetchTransaction(action.payload).pipe(
                    map((transaction: BankcardTransaction) => this.actions.setTransaction(transaction)),
                    catchError((error) => of(this.actions.setFailed(error)))
                );
            })
        );

    fetchTickets = (action$: ActionsObservable<any>) =>
        action$.ofType(BankcardTicketsActions.FETCH_TICKETS).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.fetchTickets(action.payload).pipe(
                    map((tickets: any) => this.actions.setTickets(tickets)),
                    catchError((error) => of(this.actions.setTicketsFailed(error)))
                );
            })
        );

    fetchHslTickets = (action$: ActionsObservable<any>) =>
        action$.ofType(BankcardTicketsActions.FETCH_HSL_TICKETS).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.fetchHslTickets(action.payload).pipe(
                    map((hsl: any) => {
                        let bankCardTickets = [];
                        if (hsl.charges && hsl.charges.list && hsl.charges.list.length > 0) {
                            bankCardTickets = hsl.charges.list
                                .filter(
                                    (charge) =>
                                        charge.status === 'AUTHORISED' &&
                                        charge.fares.length > 0 &&
                                        charge.fares[0].amount > 0
                                )
                                .map((charge) => {
                                    const starting = charge.fares[0].associated_transactions[0].transaction_date_time;
                                    const ending = momenttz.tz(starting, 'Europe/Helsinki').add(80, 'minutes').format();

                                    const ticket = {
                                        id: charge.id,
                                        product_name: `HSL ${charge.fares[0].associated_transactions[0].trip.stop_location.title}`,
                                        price: charge.fares[0].amount ? charge.fares[0].amount / 100 : '-',
                                        starting: starting,
                                        ending: ending
                                    };

                                    return ticket;
                                });
                        }
                        if (bankCardTickets.length > 0) {
                            return this.actions.setHslTickets(bankCardTickets);
                        } else {
                            const noTicketsError = {
                                statusCode: 404,
                                error: {
                                    sources: 'NO_TICKETS_FOUND'
                                }
                            };
                            return this.actions.setHslTicketsFailed(noTicketsError);
                        }
                    }),
                    catchError((error) => {
                        if (error.status === 404) {
                            const noTicketsError = {
                                statusCode: 404,
                                error: {
                                    sources: 'NO_TICKETS_FOUND'
                                }
                            };
                            return of(this.actions.setHslTicketsFailed(noTicketsError));
                        } else {
                            return of(this.actions.setHslTicketsFailed(error));
                        }
                    })
                );
            })
        );

    sendFeedback = (action$: ActionsObservable<any>) =>
        action$.ofType(BankcardTicketsActions.SEND_FEEDBACK).pipe(
            switchMap((action: RidangoReduxAction) => {
                return this.service.sendFeedback(action.payload).pipe(
                    map((response) => this.actions.sendFeedbackSuccess()),
                    catchError((error) => of(this.actions.setFailed(error)))
                );
            })
        );
}
